import React from 'react';
import { CardBase, CardProvider } from './useCard';
import { CardImage } from './Card.Image';
import Box, { BoxProps } from '@/polymorphics/Box';
import { CardStyleProps, useCardStyle } from './useCardStyle';
import { CardDescription } from './Card.Description';
import { CardTitle } from './Card.Title';
import { combineComponentChildren } from '@/polymorphics/utils';
import { CardBody } from './Card.Body';
import { CardFoot } from './Card.Foot';
import CardCta from './Card.Cta';
import { CardInner } from './Card.Inner';
import { CardMeta } from './Card.Meta';

export type CardProps<
  T extends CardBase = CardBase,
  D extends React.ElementType = 'article',
  P = NoProps
> = BoxProps<
  D,
  {
    item: T;
    /**
     * Remove the {@link CardLink} wrapper (linking to be added manually)
     */
    disableLinkWrap?: boolean;
    /**
     * Open the link in a new window
     */
    newWindow?: boolean;
  } & CardStyleProps &
    P
>;

export const CardRoot = <T extends CardBase = CardBase, D extends React.ElementType = 'article'>({
  component = 'article',
  item,
  disableLinkWrap,
  newWindow,
  children,
  ...rest
}: CardProps<T, D>) => {
  // Apply Card style
  const props = useCardStyle(rest);

  // The card children
  const inner = children || (
    <>
      <CardImage />
      <CardBody />
      <CardFoot />
    </>
  );

  return (
    <CardProvider value={item}>
      <Box as={component} {...props}>
        <CardInner
          {...(!disableLinkWrap &&
            !!item.href && { href: item.href, ...(newWindow && { target: '_blank' }) })}>
          {inner}
        </CardInner>
      </Box>
    </CardProvider>
  );
};

const Card = combineComponentChildren(CardRoot, {
  Body: CardBody,
  Cta: CardCta,
  Description: CardDescription,
  Meta: CardMeta,
  Foot: CardFoot,
  Image: CardImage,
  Inner: CardInner,
  Title: CardTitle,
});

export default Card;
